import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_ICON, SidebarProvider } from '@osteopro/ui';
import type { CSSProperties, ReactNode } from 'react';
import { Router } from 'wouter';
import { FirstInvoiceCelebration } from '../billing/FirstInvoiceCelebration.js';
import { MandatoryOnboardingGate } from '../onboarding/MandatoryOnboardingGate.js';
import { OnboardingRedirect } from '../onboarding/OnboardingRedirect.js';
import { TourHost } from '../onboarding/TourHost.js';
import { AppSidebar } from './AppSidebar.js';
import { Header } from './Header.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';
import { NavigationGuardDialog } from './NavigationGuardDialog.js';
import { PastDueBanner } from './PastDueBanner.js';
import { TrialBanner } from './TrialBanner.js';
import { ConsultationTabHost } from './ConsultationTabHost.js';
import { PatientTabsBar } from './PatientTabsBar.js';
import { PatientTabsProvider } from './patient-tabs.js';
import { useGuardedLocation } from './use-guarded-location.js';

/** Lu au démarrage pour rouvrir la barre latérale dans l'état où l'utilisateur l'a laissée. */
function sidebarDefaultOpen(): boolean {
  const match = /(?:^|; )sidebar_state=([^;]*)/.exec(document.cookie);
  return match?.[1] !== 'false';
}

/**
 * Coquille de tout écran authentifié. Le routeur interne reçoit la source de localisation gardée :
 * c'est ce qui fait passer **toute** navigation applicative par le dialogue de modifications
 * non enregistrées.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <PatientTabsProvider>
      <Router hook={useGuardedLocation}>
        <SidebarProvider
          defaultOpen={sidebarDefaultOpen()}
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            } as CSSProperties
          }
        >
          <div className="flex min-h-screen w-full">
            <AppSidebar />
            <div className="flex min-w-0 flex-1 flex-col">
              {/* Le bandeau d'une séance « Voir comme » colle avec l'en-tête : il reste visible. */}
              <div className="sticky top-0 z-50">
                <ImpersonationBanner />
                <Header />
              </div>
              {/* Bandeaux non bloquants : ils informent, ils ne barrent pas la route. */}
              <PastDueBanner />
              <TrialBanner />
              <PatientTabsBar />
              <main className="flex min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
                <ConsultationTabHost />
                <MandatoryOnboardingGate>{children}</MandatoryOnboardingGate>
              </main>
            </div>
          </div>
          <NavigationGuardDialog />
          {/* Mise en route : conduite vers l'assistant au premier passage, visites guidées. */}
          <OnboardingRedirect />
          <TourHost />
          <FirstInvoiceCelebration />
        </SidebarProvider>
      </Router>
    </PatientTabsProvider>
  );
}
